// src/pages/DebtStudio/lib/buildSourcesUses.js

import dayjs from "lib/dayjs";
import { dayCountFraction } from "lib/dayCount";
import { computeCOIItems, computeTotalCOI } from "./costs";
import { computeCapI } from "pages/DebtStudio/lib/computeCapI";
import { getCombinationRequirements, isCombinationPurpose } from "./combination";

/** Safe numeric */
function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

/** Best-effort extraction of firm/provider name from a COI item. */
function pickFirmName(it) {
  const candidates = [
    it?.firm,
    it?.description,
    it?.name,
    it?.vendor,
    it?.company,
    it?.counterparty,
    it?.provider,
    it?.party,
    it?.label,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
}

/** Collapse COI items by type but retain children (with firm) for expansion */
function aggregateCOIByType(costs = [], finalPar) {
  const items = computeCOIItems(costs, finalPar);

  const grouped = new Map();
  for (const it of items) {
    const type = String(it?.type || "").trim() || "Other COI";
    const firm = pickFirmName(it);
    const amount = num(it?.computedAmount, 0);

    if (!grouped.has(type)) {
      grouped.set(type, { label: type, amount: 0, children: [] });
    }
    const group = grouped.get(type);
    group.amount += amount;
    group.children.push({ type, firm, amount });
  }

  return Array.from(grouped.values()).map((g) => ({
    ...g,
    expanded: false,
  }));
}

function buildRefundingDepositUse(form, amount, { manualOnly = false } = {}) {
  const entries = Array.isArray(form?.refunded_issues)
    ? form.refunded_issues
    : Array.isArray(form?.refundingManualDeposits)
    ? form.refundingManualDeposits
    : [];

  const children = entries.flatMap((entry, index) => {
    const entryType = norm(entry?.entry_type ?? entry?.entryType ?? "manual");
    if (manualOnly && entryType === "ds_issue") return [];

    const detailAmount = num(
      entry?.deposit_amount ??
        entry?.depositAmount ??
        entry?.amount ??
        entry?.deposit ??
        entry?.deposit_required ??
        entry?.depositRequirement,
      0
    );
    if (!(detailAmount > 0)) return [];

    const issueName = String(
      entry?.issue_name ??
        entry?.issueName ??
        entry?.name ??
        entry?.label ??
        `Refunded Issue ${index + 1}`
    ).trim();

    return [{
      type: "Refunding Deposit",
      firm: issueName || `Refunded Issue ${index + 1}`,
      amount: detailAmount,
    }];
  });

  return {
    label: "Refunding Deposit",
    amount,
    ...(children.length ? { children, expanded: false } : {}),
  };
}

/** Dollars → cents rounding */
function roundCents(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function norm(v) {
  return String(v ?? "").trim().toLowerCase();
}

/** Helper to interpret coupon values as % or decimal. */
function parseCouponAnnual(v) {
  if (v == null || v === "") return 0;
  let n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if (n > 1) n = n / 100;
  if (n < 0) return 0;
  return n;
}

/**
 * Compute settlement accrued interest as:
 *
 *   Σ principal_j × coupon_j × DCF(datedDate, deliveryDate)
 *
 * This is shown as both a source and use of funds.
 */
function computeAccruedInterest({ rows = [], form = {} }) {
  const datedDate = form?.datedDate ?? form?.dated_date ?? null;
  const deliveryDate = form?.deliveryDate ?? form?.delivery_date ?? null;
  const dayCount = form?.dayCount ?? form?.day_count ?? "30/360";

  const dd = datedDate ? dayjs(datedDate).startOf("day") : null;
  const dlv = deliveryDate ? dayjs(deliveryDate).startOf("day") : null;

  if (!dd || !dlv || !dd.isValid() || !dlv.isValid()) return 0;
  if (!dlv.isAfter(dd, "day")) return 0;

  const frac = dayCountFraction(dd.toDate(), dlv.toDate(), dayCount);
  if (!Number.isFinite(frac) || frac <= 0) return 0;

  const accrued = rows.reduce((sum, r) => {
    const principal = num(r?.principal, 0);
    if (principal <= 0) return sum;

    const couponAnnual = parseCouponAnnual(r?.coupon);
    if (couponAnnual <= 0) return sum;

    return sum + principal * couponAnnual * frac;
  }, 0);

  return roundCents(accrued);
}

/**
 * Build Sources & Uses entirely from DealStructure data
 */
export function buildSourcesUses(form = {}, structureRows = []) {
  const dealTypeStr = norm(form?.deal_type || form?.dealType || "");
  const isRefundingDeal = dealTypeStr === "refunding";
  const isCombinationDeal = isCombinationPurpose(dealTypeStr);

  const dealClassStr = norm(form?.deal_class ?? form?.dealClass ?? "");
  const isLibraryDeal = dealClassStr === "library";

  /** Final Par */
  const finalPar = structureRows.reduce((sum, r) => sum + num(r?.principal, 0), 0);

  /** Additional Sources */
  const additionalSources = Array.isArray(form?.additionalSources)
    ? form.additionalSources
    : [];

  const totalAdditionalSources = additionalSources.reduce(
    (sum, s) => sum + num(s?.amount, 0),
    0
  );

  /** COI */
  const totalCOI = computeTotalCOI(form?.costOfIssuance || [], finalPar);

  /** Normalize term prices */
  let normalizedRows = structureRows;
  if (Array.isArray(structureRows) && structureRows.length) {
    const termGroups = {};
    for (const r of structureRows) {
      if (r?.bond_type === "term" && r?.term_group) {
        if (!termGroups[r.term_group]) termGroups[r.term_group] = [];
        termGroups[r.term_group].push(r);
      }
    }

    const sharedPriceByGroup = {};
    Object.values(termGroups).forEach((members) => {
      members.sort((a, b) => String(a?.date).localeCompare(String(b?.date)));
      const last = members[members.length - 1];
      if (last?.price != null) {
        sharedPriceByGroup[last.term_group] = Number(last.price);
      }
    });

    normalizedRows = structureRows.map((r) => {
      if (
        r?.bond_type === "term" &&
        r?.term_group &&
        sharedPriceByGroup[r.term_group]
      ) {
        return { ...r, price: sharedPriceByGroup[r.term_group] };
      }
      return r;
    });
  }

  /** Gross Proceeds */
  const bondProceeds = normalizedRows.reduce((sum, r) => {
    return sum + num(r?.principal, 0) * (num(r?.price, 100) / 100);
  }, 0);

  /** Premium / Discount */
  const netPremiumRaw = bondProceeds - finalPar;
  const netPremium = Math.abs(netPremiumRaw) < 0.005 ? 0 : netPremiumRaw;

  /** Accrued Interest */
  const accruedInterest = computeAccruedInterest({
    rows: normalizedRows,
    form,
  });

  /** CapI */
  let capiUse = 0;
  try {
    const capi = computeCapI({
      dealType: form?.deal_type || form?.dealType,
      capiEnabled: form?.capiEnabled ?? form?.capi_enabled ?? false,
      capiDate: form?.capiDate ?? form?.capi_date ?? "",
      capiInvestmentRate:
        form?.capiInvestmentRate ?? form?.capi_investment_rate ?? 0,
      rows: normalizedRows,
      form: {
        datedDate: form?.datedDate ?? form?.dated_date ?? null,
        deliveryDate: form?.deliveryDate ?? form?.delivery_date ?? null,
        firstCouponDate: form?.firstCouponDate ?? form?.first_coupon_date ?? null,
        finalMaturityDate:
          form?.finalMaturityDate ?? form?.final_maturity_date ?? null,
        couponFrequency:
          form?.couponFrequency ?? form?.coupon_frequency ?? "semiannual",
        dayCount: form?.dayCount ?? form?.day_count ?? "30/360",
      },
    });

    capiUse = roundCents(Number(capi?.capiUse || 0));
  } catch {
    capiUse = 0;
  }

  /** Sources */
  const sources = [
    {
      label:
        norm(form?.financingType || form?.financing_type) === "loan"
          ? "Principal Amount"
          : "Par Amount",
      amount: finalPar,
    },
    ...(netPremium > 0
      ? [{ label: "Original Issue Premium", amount: netPremium }]
      : netPremium < 0
      ? [{ label: "Less: Original Issue Discount", amount: netPremium }]
      : []),
    ...(accruedInterest > 0
      ? [{ label: "Accrued Interest Received", amount: accruedInterest }]
      : []),
    ...additionalSources.map((s, i) => ({
      label: s?.source || `Additional Source ${i + 1}`,
      amount: num(s?.amount, 0),
    })),
  ];

  /** Uses */
  const coiGroups = aggregateCOIByType(form?.costOfIssuance || [], finalPar);

  const actualNetRaw = bondProceeds - totalCOI - capiUse + totalAdditionalSources;
  const actualNet = Math.abs(actualNetRaw) < 0.005 ? 0 : actualNetRaw;

  let uses = [];

  if (isRefundingDeal) {
    if (isLibraryDeal) {
      uses = [
        buildRefundingDepositUse(form, Math.max(0, actualNet)),
        ...(accruedInterest > 0
          ? [{ label: "Accrued Interest", amount: accruedInterest }]
          : []),
        ...coiGroups,
      ];
    } else {
      const refundingDepositTarget = num(form?.targetValue, 0);
      const residual = actualNet - refundingDepositTarget;

      uses = [
        buildRefundingDepositUse(form, refundingDepositTarget),
        ...(accruedInterest > 0
          ? [{ label: "Accrued Interest", amount: accruedInterest }]
          : []),
        ...coiGroups,
        ...(residual > 0.005
          ? [{ label: "Unallocated Proceeds", amount: residual }]
          : []),
      ];
    }
  } else if (isCombinationDeal) {
    const requirements = getCombinationRequirements(form);
    const targetsPrincipal = norm(form?.targetType ?? form?.target_type) === "par_amount";
    const projectFund = targetsPrincipal
      ? Math.max(0, actualNet - requirements.refundingRequirement)
      : requirements.newMoneyRequirement;
    const residual = targetsPrincipal
      ? 0
      : actualNet - requirements.totalRequirement;
    uses = [
      { label: "New Money Project Fund", amount: projectFund },
      buildRefundingDepositUse(form, requirements.refundingRequirement, { manualOnly: true }),
      ...(capiUse > 0 ? [{ label: "Capitalized Interest Fund", amount: capiUse }] : []),
      ...(accruedInterest > 0 ? [{ label: "Accrued Interest", amount: accruedInterest }] : []),
      ...coiGroups,
      ...(residual > 0.005 ? [{ label: "Unallocated Proceeds", amount: residual }] : []),
    ];
  } else {
    uses = [
      { label: "Net Proceeds", amount: Math.max(0, actualNet) },
      ...(capiUse > 0
        ? [{ label: "Capitalized Interest Fund", amount: capiUse }]
        : []),
      ...(accruedInterest > 0
        ? [{ label: "Accrued Interest", amount: accruedInterest }]
        : []),
      ...coiGroups,
    ];
  }

  return { sources, uses };
}
